import { useParams } from '@tanstack/react-router';
import { useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { T, useGT, Var } from 'gt-react';
import { isAppViewSpec } from '@erp/json-renderer/apps';
import { AppView } from '@erp/json-renderer/apps/react';
import type { AppDetails, OpenedApp } from '@erp/rpc';
import {
  useAppDetails,
  useOpenApp,
  useRunAppCommand,
  useSetAppAutoUpdate,
} from '@/api/rpc/hooks/apps/useApps';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { HeaderPageTitle } from '@/context/header-title.context';
import { useAppsAgentModule } from '../useAppsAgentModule';
import { createAppController } from './appController';
import { createAppBehaviorHost } from './appBehaviorHost';
import { createExecutionDiagnostics } from './appLiveState';

/**
 * One App: its view, rendered with the Gently component set over the feeds its
 * active version loaded, plus its settings, versions and repair history.
 */
export function AppPage() {
  const { appId } = useParams({ from: '/authenticated/apps/$appId' });
  const opened = useOpenApp(appId);

  return (
    <div className="mx-auto grid w-full max-w-6xl gap-4 p-4 lg:grid-cols-[1fr_20rem]">
      {(!opened.data || opened.error) && <LoadingAppsAssistant appId={appId} />}
      <div className="min-w-0 space-y-3">
        {opened.error ? (
          <ErrorBox error={opened.error} />
        ) : opened.data ? (
          <OpenedAppView
            key={`${appId}:${opened.data.deployment.id}:${opened.dataUpdatedAt}`}
            opened={opened.data}
          />
        ) : (
          <div className="space-y-2">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}
      </div>
      <AppSidePanel appId={appId} opened={!opened.isPending} />
    </div>
  );
}

function LoadingAppsAssistant({ appId }: { appId: string }) {
  useAppsAgentModule(appId);
  return null;
}

function ErrorBox({ error }: { error: unknown }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {error instanceof Error ? error.message : String(error)}
    </div>
  );
}

function OpenedAppView({ opened }: { opened: OpenedApp }) {
  const runtime = useAppRuntime(opened);
  useLayoutEffect(() => runtime.connect(), [runtime]);
  useAppsAgentModule(opened.app.id, runtime.controller, runtime.behaviors);
  return (
    <>
      <HeaderPageTitle>{opened.app.name}</HeaderPageTitle>
      <AppViewBody opened={opened} runtime={runtime} />
    </>
  );
}

type AppRuntime = ReturnType<typeof useAppRuntime>;

/** An App's controller, and its behavior host when it has a plan. */
function useAppRuntime(opened: OpenedApp) {
  const runCommand = useRunAppCommand(opened.app.id);
  const gt = useGT();
  const [runtime] = useState(() => {
    const controller = createAppController({
      opened,
      run: runCommand.mutateAsync,
      notify: (message) => toast.error(message),
      busyMessage: gt(
        'Wait for the current save to finish before trying again.',
        {
          context:
            'App action rejected because another write is still in flight',
        },
      ),
    });
    // A v1 App has no plan and keeps the controller's plain store and handlers.
    const behaviors = opened.behaviors
      ? createAppBehaviorHost({
          plan: opened.behaviors,
          controller,
          ownedRoots: Object.keys(opened.state).map((key) => `/${key}`),
        })
      : null;
    return {
      controller,
      behaviors,
      diagnostics: createExecutionDiagnostics(controller, behaviors),
      store: behaviors?.store ?? controller.store,
      handlers: behaviors
        ? { ...controller.handlers, ...behaviors.handlers }
        : controller.handlers,
      connect: () => {
        // A StrictMode replay cleans up and sets up again, so the host
        // reconnects under a fresh lease instead of staying disposed.
        const disconnectBehaviors = behaviors?.connect() ?? null;
        const disconnect = controller.connect();
        return () => {
          disconnectBehaviors?.();
          disconnect();
        };
      },
    };
  });
  return runtime;
}

function AppViewBody({
  opened,
  runtime,
}: {
  opened: OpenedApp;
  runtime: AppRuntime;
}) {
  const { controller, diagnostics, store, handlers } = runtime;
  const feedErrors = useSyncExternalStore(
    controller.store.subscribe,
    controller.getFeedErrors,
    controller.getFeedErrors,
  );

  return (
    <>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline">
          <T context="App version badge">
            Version <Var>{opened.deployment.version}</Var>
          </T>
        </Badge>
        {opened.deployment.origin === 'auto_repair' ? (
          <Badge variant="secondary">
            <T context="badge on an App version deployed by an automatic fix">
              Automatic fix
            </T>
          </Badge>
        ) : null}
      </div>
      {feedErrors.length > 0 ? (
        <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
          <T context="App page: some of the App's data failed to load">
            Some of this App's data could not be loaded:
          </T>
          <ul className="mt-1 list-disc pl-5">
            {feedErrors.map((error) => (
              <li key={error.key}>
                <span className="font-mono">{error.key}</span> ({error.toolType}
                ): {error.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {isAppViewSpec(opened.view) ? (
        <AppView
          spec={opened.view}
          store={store}
          handlers={handlers}
          diagnostics={diagnostics}
        />
      ) : null}
    </>
  );
}

function AppSidePanel({ appId, opened }: { appId: string; opened: boolean }) {
  const { data: details } = useAppDetails(appId, { enabled: opened });
  return (
    <aside className="space-y-4">
      {details ? (
        <>
          <AutoUpdateSetting details={details} />
          <VersionList details={details} />
          <RepairList details={details} />
        </>
      ) : (
        <Skeleton className="h-40 w-full" />
      )}
    </aside>
  );
}

function AutoUpdateSetting({ details }: { details: AppDetails }) {
  const gt = useGT();
  // Authorized server-side (App update); a refusal comes back as a toast.
  const setAutoUpdate = useSetAppAutoUpdate(details.app.id);

  return (
    <section className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-start gap-3">
        <Switch
          id="app-auto-update"
          checked={details.app.autoUpdateEnabled}
          disabled={setAutoUpdate.isPending}
          onCheckedChange={(enabled) =>
            setAutoUpdate.mutate(enabled, {
              onError: (error) => toast.error(error.message),
            })
          }
        />
        <Label htmlFor="app-auto-update" className="text-sm leading-snug">
          <T context="App setting: toggle for automatic repairs and updates">
            Automatically fix errors and keep the App up to date with the latest
            Gently tools
          </T>
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {gt(
          'When the same fix works three times, Gently saves it as a new version. You are notified either way.',
          { context: 'help text under the App automatic-fix toggle' },
        )}
      </p>
    </section>
  );
}

function VersionList({ details }: { details: AppDetails }) {
  return (
    <section className="space-y-2 rounded-md border bg-card p-3">
      <h2 className="text-sm font-medium">
        <T context="App side panel heading">Versions</T>
      </h2>
      <ul className="space-y-1 text-xs">
        {details.deployments.map((deployment) => (
          <li key={deployment.id} className="flex items-center gap-2">
            <span className="shrink-0 font-mono">
              <T context="App version number, short">
                v<Var>{deployment.version}</Var>
              </T>
            </span>
            {deployment.status === 'active' ? (
              <Badge variant="secondary" className="shrink-0">
                <T context="App version status">Active</T>
              </Badge>
            ) : null}
            {deployment.origin === 'auto_repair' ? (
              <Badge variant="outline" className="shrink-0">
                <T context="App version origin">Automatic fix</T>
              </Badge>
            ) : null}
            <span
              className="min-w-0 truncate text-muted-foreground"
              title={deployment.changeNote ?? undefined}
            >
              {deployment.changeNote ?? ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RepairList({ details }: { details: AppDetails }) {
  return (
    <section className="space-y-2 rounded-md border bg-card p-3">
      <h2 className="text-sm font-medium">
        <T context="App side panel heading">Tool-call repairs</T>
      </h2>
      {details.repairs.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="App side panel: no repairs yet">
            No failed tool calls so far.
          </T>
        </p>
      ) : (
        <ul className="space-y-2 text-xs">
          {details.repairs.map((repair) => (
            <li key={repair.id} className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Badge
                  variant={
                    repair.outcome === 'repaired' ? 'secondary' : 'outline'
                  }
                >
                  {repair.outcome}
                </Badge>
                <span className="font-mono">{repair.callSite}</span>
                {repair.httpStatus ? (
                  <span className="text-muted-foreground">
                    {repair.httpStatus}
                  </span>
                ) : null}
              </div>
              <p className="text-muted-foreground">
                {repair.explanation ?? repair.errorMessage}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
